import { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OfflineBanner from './OfflineBanner';
import { useNativeNotificationRouting } from '../hooks/useNativeNotificationRouting';

const navItems = [
  {
    to: '/',
    label: 'Home',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    to: '/swipe',
    label: 'Recipes',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M8 12h8" />
        <path d="M12 8v8" />
      </svg>
    ),
  },
  {
    to: '/planner',
    label: 'Plan',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 3v18" />
        <path d="M15 3v18" />
      </svg>
    ),
  },
  {
    to: '/stats',
    label: 'Stats',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M4 22h16" />
        <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22" />
        <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22" />
        <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
      </svg>
    ),
  },
];

export default function AppLayout() {
  const location = useLocation();
  const { currentMember, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useNativeNotificationRouting();

  const isFamilyActive = location.pathname === '/family';
  const isSettingsActive = location.pathname === '/settings';

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    await signOut();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-canvas to-canvas flex justify-center">
      <div className="w-full max-w-md lg:max-w-6xl flex flex-col min-h-screen relative">
        {/* Glassmorphic header */}
        <header className="flex items-center gap-3 px-5 lg:px-8 py-4 bg-surface/70 backdrop-blur-md sticky top-0 z-30">
          <div className="flex items-center gap-2.5">
            <svg viewBox="0 0 32 32" className="w-7 h-7" fill="none" aria-hidden="true">
              <circle cx="16" cy="16" r="13" stroke="var(--dl-primary)" strokeWidth="2" />
              <circle cx="16" cy="16" r="6.5" stroke="var(--dl-primary)" strokeWidth="2" />
            </svg>
            <h1 className="font-serif text-xl text-ink">Suppertide</h1>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center gap-1 ml-8">
            {navItems.map(({ to, label, icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-xl font-body text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-panel text-lime shadow-sm'
                      : 'text-ink-soft hover:text-ink hover:bg-ink/5'
                  }`
                }
              >
                {icon}
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Account menu */}
          <div className="ml-auto relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${
                menuOpen || isFamilyActive || isSettingsActive
                  ? 'bg-panel border-lime/40 text-lime'
                  : 'bg-surface border-hairline text-ink-soft hover:text-ink hover:bg-ink/5'
              }`}
              aria-label="Account menu"
              aria-expanded={menuOpen}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {currentMember && (
                <span className="font-body text-sm font-medium hidden sm:inline max-w-[100px] truncate">
                  {currentMember.name?.split(' ')[0]}
                </span>
              )}
              <svg className={`w-3 h-3 transition-transform ${menuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m19 9-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-panel rounded-xl shadow-lg shadow-glow-sm border border-hairline py-1 z-50">
                <NavLink
                  to="/family"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 font-body text-sm transition-colors ${
                      isActive ? 'text-lime bg-lime/10' : 'text-ink-soft hover:bg-surface'
                    }`
                  }
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  Family Members
                </NavLink>
                <NavLink
                  to="/settings"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 font-body text-sm transition-colors ${
                      isActive ? 'text-lime bg-lime/10' : 'text-ink-soft hover:bg-surface'
                    }`
                  }
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9" />
                  </svg>
                  Settings
                </NavLink>
                <div className="border-t border-hairline my-1" />
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-2.5 font-body text-sm text-ink-soft hover:bg-surface hover:text-red-500 transition-colors"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </header>

        <OfflineBanner />

        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto px-4 lg:px-8 py-4 lg:py-6 pb-28 lg:pb-6">
          <Outlet />
        </main>

        {/* Floating pill navigation bar - mobile only */}
        <div className="fixed bottom-0 left-0 right-0 z-40 flex justify-center pb-safe-bottom pointer-events-none lg:hidden">
          <div className="max-w-md w-full px-5 pb-4">
            <nav className="bg-surface/90 backdrop-blur-xl rounded-2xl shadow-lg shadow-glow-sm border border-hairline px-2 py-2 flex justify-around items-center pointer-events-auto">
              {navItems.map(({ to, label, icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) =>
                    `relative flex flex-col items-center gap-0.5 px-4 py-2 rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'text-lime'
                        : 'text-ink-muted hover:text-ink-soft'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-5 h-1 bg-lime rounded-full" />
                      )}
                      {icon}
                      <span className="text-[10px] font-body font-medium">{label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
